import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Printer, Loader2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { vendorInvoiceApi } from '../../../services/vendorInvoiceService';
import { useVendorProfile } from '../../../services/vendorProfileService';
import { useToastContext } from '../../../contexts/ToastContext';
import Button from '../../shared/Button';
import Input from '../../shared/Input';
import CustomSelect from '../../shared/CustomSelect';
import DatePicker from '../../client/DatePicker';
import InvoiceLivePreview from './InvoiceLivePreview';

const emptyLine = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  description: '',
  quantity: '1',
  unit_price: '',
});

const lineItemsFromBooking = (booking) => {
  if (!booking) return [emptyLine()];

  const description =
    booking.line_item_description ||
    [booking.service_name, booking.package_name].filter(Boolean).join(' — ') ||
    'Booking service';

  const price =
    booking.line_item_unit_price ??
    booking.subtotal_amount ??
    null;

  return [
    {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      description,
      quantity: '1',
      unit_price: price != null && price !== '' ? String(price) : '',
    },
  ];
};

const addDaysIso = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const CustomInvoiceBuilder = () => {
  const navigate = useNavigate();
  const { id: editInvoiceId } = useParams();
  const isEdit = Boolean(editInvoiceId);
  const queryClient = useQueryClient();
  const { success, error: showError } = useToastContext();
  const { data: profileResp } = useVendorProfile();

  const vp = profileResp?.data ?? {};
  const vendorLabel = vp.business_name || 'Your business';
  const vendorAddressLines = [
    vp.address,
    [vp.city, vp.state, vp.zip_code].filter(Boolean).join(', '),
    vp.country,
    vp.business_phone,
  ].filter(Boolean);

  const [clientId, setClientId] = useState('');
  const [bookingId, setBookingId] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState(() => addDaysIso(14));
  const [taxRate, setTaxRate] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [status, setStatus] = useState('draft');
  const [lineItems, setLineItems] = useState([emptyLine()]);
  const [previewInvoiceNumber, setPreviewInvoiceNumber] = useState('');
  const hydratedInvoiceIdRef = useRef(null);

  const { data: invoiceRes, isLoading: loadingInvoice, error: invoiceError } = useQuery({
    queryKey: ['vendor-invoice', editInvoiceId],
    queryFn: () => vendorInvoiceApi.getOne(editInvoiceId),
    enabled: isEdit,
  });

  const { data: clientsRes, isLoading: clientsLoading } = useQuery({
    queryKey: ['invoice-billable-clients'],
    queryFn: () => vendorInvoiceApi.getBillableClients(),
  });

  const clients = useMemo(() => clientsRes?.data ?? [], [clientsRes?.data]);

  const { data: bookingsRes, isLoading: bookingsLoading } = useQuery({
    queryKey: ['invoice-booking-options', clientId],
    queryFn: () => vendorInvoiceApi.getBookingOptions(clientId || null),
    enabled: !!clientId,
  });

  const bookings = useMemo(() => bookingsRes?.data ?? [], [bookingsRes?.data]);

  useEffect(() => {
    if (isEdit) return;
    setBookingId('');
  }, [clientId, isEdit]);

  useEffect(() => {
    hydratedInvoiceIdRef.current = null;
  }, [editInvoiceId]);

  useEffect(() => {
    if (!isEdit || !invoiceRes?.data) return;
    const inv = invoiceRes.data;
    if (inv.id !== editInvoiceId) return;
    if (hydratedInvoiceIdRef.current === inv.id) return;
    if (['paid', 'cancelled'].includes(String(inv.status || '').toLowerCase())) {
      showError('This invoice cannot be edited.');
      navigate(`/vendor/invoices/${editInvoiceId}`, { replace: true });
      return;
    }
    hydratedInvoiceIdRef.current = inv.id;
    setClientId(inv.client_id || '');
    setBookingId(inv.booking_id || '');
    const idate = inv.invoice_date ? String(inv.invoice_date).slice(0, 10) : '';
    const ddate = inv.due_date ? String(inv.due_date).slice(0, 10) : '';
    setInvoiceDate(idate || new Date().toISOString().slice(0, 10));
    setDueDate(ddate || addDaysIso(14));
    setTaxRate(inv.tax_rate != null && inv.tax_rate !== '' ? String(inv.tax_rate) : '');
    setNotes(inv.notes || '');
    setPaymentTerms(inv.payment_terms || '');
    setStatus(inv.status || 'draft');
    setPreviewInvoiceNumber(inv.invoice_number || '');
    const raw = inv.line_items;
    if (raw?.length) {
      setLineItems(
        raw.map((li, i) => ({
          id: `edit-${i}-${li.description?.slice(0, 8) || i}`,
          description: li.description ?? '',
          quantity: String(li.quantity ?? 1),
          unit_price: li.unit_price != null ? String(li.unit_price) : '',
        }))
      );
    } else {
      setLineItems([emptyLine()]);
    }
  }, [isEdit, invoiceRes, editInvoiceId, navigate, showError]);

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === clientId),
    [clients, clientId]
  );

  const selectedBooking = useMemo(
    () => bookings.find((b) => b.id === bookingId),
    [bookings, bookingId]
  );

  const { subtotal, taxAmount, total, taxRateNum } = useMemo(() => {
    const sub = lineItems.reduce((sum, row) => {
      const q = parseFloat(row.quantity) || 0;
      const u = parseFloat(row.unit_price) || 0;
      return sum + q * u;
    }, 0);
    const tr = parseFloat(taxRate) || 0;
    const tax = sub * (tr / 100);
    return {
      subtotal: sub,
      taxAmount: tax,
      total: sub + tax,
      taxRateNum: tr,
    };
  }, [lineItems, taxRate]);

  const clientOptions = useMemo(
    () => clients.map((c) => ({ value: c.id, label: `${c.name || 'Client'} · ${c.email || ''}` })),
    [clients]
  );

  const bookingOptions = useMemo(() => {
    const opts = [{ value: '', label: 'No linked booking' }];
    bookings.forEach((b) => {
      const ref = b.booking_reference || b.id?.slice(0, 8);
      const svc = b.service_name ? ` · ${b.service_name}` : '';
      opts.push({
        value: b.id,
        label: `${ref}${svc}${b.event_date ? ` · ${b.event_date}` : ''}`,
      });
    });
    return opts;
  }, [bookings]);

  const createMutation = useMutation({
    mutationFn: (payload) => vendorInvoiceApi.createCustom(payload),
    onSuccess: (res) => {
      success(res?.message || 'Invoice created.');
      queryClient.invalidateQueries({ queryKey: ['vendorInvoices'] });
      const newId = res?.data?.id;
      if (newId) navigate(`/vendor/invoices/${newId}`, { replace: true });
      else navigate('/vendor/invoices');
    },
    onError: (e) => {
      const msg = e?.response?.data?.message || 'Failed to create invoice.';
      const errs = e?.response?.data?.errors;
      if (errs && typeof errs === 'object') {
        const first = Object.values(errs).flat()[0];
        showError(first || msg);
      } else {
        showError(msg);
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload) => vendorInvoiceApi.update(editInvoiceId, payload),
    onSuccess: (res) => {
      success(res?.message || 'Invoice updated.');
      queryClient.invalidateQueries({ queryKey: ['vendorInvoices'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-invoice', editInvoiceId] });
      navigate(`/vendor/invoices/${editInvoiceId}`, { replace: true });
    },
    onError: (e) => {
      const msg = e?.response?.data?.message || 'Failed to update invoice.';
      const errs = e?.response?.data?.errors;
      if (errs && typeof errs === 'object') {
        const first = Object.values(errs).flat()[0];
        showError(first || msg);
      } else {
        showError(msg);
      }
    },
  });

  const updateLine = (id, field, value) => {
    setLineItems((rows) =>
      rows.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    );
  };

  const addLine = () => setLineItems((rows) => [...rows, emptyLine()]);
  const removeLine = (id) =>
    setLineItems((rows) => (rows.length <= 1 ? rows : rows.filter((r) => r.id !== id)));

  const handleBookingChange = (value) => {
    setBookingId(value);
    if (!value) return;
    const booking = bookings.find((b) => b.id === value);
    if (booking) {
      setLineItems(lineItemsFromBooking(booking));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!clientId) {
      showError('Select a client.');
      return;
    }
    const payloadLines = lineItems
      .map((row) => ({
        description: row.description.trim(),
        quantity: parseFloat(row.quantity),
        unit_price: parseFloat(row.unit_price),
      }))
      .filter((row) => row.description && !Number.isNaN(row.quantity) && !Number.isNaN(row.unit_price));

    if (payloadLines.length === 0) {
      showError('Add at least one line item with description and valid amounts.');
      return;
    }

    const payload = {
      client_id: clientId,
      booking_id: bookingId || null,
      invoice_date: invoiceDate,
      due_date: dueDate,
      tax_rate: taxRate === '' ? null : parseFloat(taxRate),
      notes: notes.trim() || null,
      payment_terms: paymentTerms.trim() || null,
      status,
      line_items: payloadLines,
    };

    if (isEdit) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDisplayDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso + 'T12:00:00').toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  if (isEdit && loadingInvoice) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-10 h-10 animate-spin text-gray-400" />
      </div>
    );
  }

  if (isEdit && invoiceError) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-700 mb-4">Could not load invoice.</p>
        <Button type="button" variant="outline" onClick={() => navigate('/vendor/invoices')}>
          Back to invoices
        </Button>
      </div>
    );
  }

  if (clientsLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-10 h-10 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!clients.length && !isEdit) {
    return (
      <div className="max-w-lg mx-auto text-center py-16 px-4">
        <p className="text-gray-700 mb-2">No clients available for invoicing yet.</p>
        <p className="text-sm text-gray-500 mb-6">
          Custom invoices can only be sent to clients who already have a booking with you.
        </p>
        <Button type="button" variant="outline" onClick={() => navigate('/vendor/invoices')}>
          Back to invoices
        </Button>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @media print {
          .invoice-no-print { display: none !important; }
          .invoice-print-area { display: block !important; width: 100% !important; max-width: none !important; }
        }
      `}</style>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 xl:gap-10 items-start">
        <form onSubmit={handleSubmit} className="space-y-6 invoice-no-print">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <CustomSelect
              label="Client *"
              value={clientId}
              onChange={(v) => setClientId(v)}
              options={clientOptions}
              placeholder="Select a client"
              size="sm"
            />
            <CustomSelect
              label="Link booking (optional)"
              value={bookingId}
              onChange={handleBookingChange}
              options={bookingOptions}
              placeholder={clientId ? (bookingsLoading ? 'Loading…' : 'Optional') : 'Select client first'}
              disabled={!clientId || bookingsLoading}
              size="sm"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DatePicker
              label="Invoice date *"
              value={invoiceDate}
              onChange={setInvoiceDate}
              placeholder="Select date"
              allowPastDates
            />
            <DatePicker
              label="Due date *"
              value={dueDate}
              onChange={setDueDate}
              placeholder="Select date"
              allowPastDates
              minDate={invoiceDate || undefined}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-900">Line items *</label>
              <button
                type="button"
                onClick={addLine}
                className="inline-flex items-center gap-1 text-sm font-medium text-gray-800 hover:text-black"
              >
                <Plus className="w-4 h-4" />
                Add line
              </button>
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-100">
              {lineItems.map((row, index) => (
                <div key={row.id} className="p-3 sm:p-4 bg-white space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-xs font-medium text-gray-500 uppercase">Line {index + 1}</span>
                    {lineItems.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLine(row.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded-md hover:bg-red-50"
                        aria-label="Remove line"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <Input
                    value={row.description}
                    onChange={(e) => updateLine(row.id, 'description', e.target.value)}
                    placeholder="Description (e.g. Photo booth — 4 hours)"
                    className="w-full"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Qty</label>
                      <Input
                        type="number"
                        min="0.01"
                        step="any"
                        value={row.quantity}
                        onChange={(e) => updateLine(row.id, 'quantity', e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Unit price ($)</label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.unit_price}
                        onChange={(e) => updateLine(row.id, 'unit_price', e.target.value)}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1">Tax rate (%)</label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                placeholder="0"
              />
            </div>
            <CustomSelect
              label="Status"
              value={status}
              onChange={setStatus}
              options={
                isEdit
                  ? [
                      { value: 'draft', label: 'Draft' },
                      { value: 'sent', label: 'Sent' },
                      { value: 'overdue', label: 'Overdue' },
                    ]
                  : [
                      { value: 'draft', label: 'Draft' },
                      { value: 'sent', label: 'Sent' },
                    ]
              }
              size="sm"
              className="!min-h-[2.75rem]"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-1">Payment terms (optional)</label>
            <textarea
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
              placeholder="e.g. Net 14, due on receipt"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
              placeholder="Thank you for your business"
            />
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <Button
              type="submit"
              loading={createMutation.isPending || updateMutation.isPending}
              disabled={createMutation.isPending || updateMutation.isPending}
              icon={createMutation.isPending || updateMutation.isPending ? Loader2 : undefined}
            >
              {isEdit ? 'Save changes' : 'Create invoice'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                navigate(isEdit ? `/vendor/invoices/${editInvoiceId}` : '/vendor/invoices')
              }
            >
              Cancel
            </Button>
            <Button type="button" variant="outline" onClick={handlePrint} icon={Printer}>
              Print preview
            </Button>
          </div>
        </form>

        <div className="xl:sticky xl:top-24 space-y-3 invoice-print-area">
          <div className="flex items-center justify-between invoice-no-print">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Live preview</h3>
            <span className="text-xs text-gray-400">Updates as you type</span>
          </div>
          <InvoiceLivePreview
            vendorLabel={vendorLabel}
            vendorAddressLines={vendorAddressLines}
            billToName={selectedClient?.name}
            billToEmail={selectedClient?.email}
            billToPhone={selectedClient?.phone}
            invoiceNumberPreview={isEdit && previewInvoiceNumber ? previewInvoiceNumber : '(assigned on save)'}
            invoiceDate={formatDisplayDate(invoiceDate)}
            dueDate={formatDisplayDate(dueDate)}
            lineItems={lineItems.map((r) => ({
              description: r.description,
              quantity: parseFloat(r.quantity) || 0,
              unit_price: parseFloat(r.unit_price) || 0,
            }))}
            taxRate={taxRateNum}
            subtotal={subtotal}
            taxAmount={taxAmount}
            total={total}
            notes={notes}
            paymentTerms={paymentTerms}
            bookingRef={
              selectedBooking
                ? selectedBooking.booking_reference || selectedBooking.id?.slice(0, 8)
                : null
            }
            statusLabel={
              status === 'sent' ? 'Sent' : status === 'overdue' ? 'Overdue' : 'Draft'
            }
          />
        </div>
      </div>
    </>
  );
};

export default CustomInvoiceBuilder;
